// frontend/admin-portal/src/pages/RfqComparison.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
} from "@fiberhome/shared-ui";
import { requisitionService } from "../services/requisitionService";
import ComparisonTable from "../components/procurement/ComparisonTable";
import ComparisonSummaryCards from "../components/procurement/ComparisonSummaryCards";
import RequisitionStatusBadge from "../components/procurement/RequisitionStatusBadge";
import AwardQuotationModal from "../components/procurement/AwardQuotationModal";
import {
  MarginBadge,
  computeMargin,
  formatMoney,
  computeProfit,
} from "@fiberhome/shared-ui";

export default function RfqComparison() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Award modal state
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardData, setAwardData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await requisitionService.comparison(id);
      setComparison(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openAward = (supplierData) => {
    setAwardData(supplierData);
    setAwardOpen(true);
  };

  const closeAward = () => {
    setAwardOpen(false);
    setAwardData(null);
  };

  const handleAwarded = () => {
    // Reload the comparison so the award flag / supplier status reflects the change
    load();
  };

  // ---- Loading / error ----
  if (loading) {
    return (
      <>
        <PageHeader
          title="Quote Comparison"
          breadcrumbs={["Home", "Procurement", "…"]}
        />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !comparison) {
    return (
      <>
        <PageHeader title="Comparison" />
        <Alert variant="danger">{error || "Not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/requisitions")}
        >
          ← Back to Requisitions
        </Button>
      </>
    );
  }

  const {
    requisition,
    suppliers = [],
    items = [],
    globallyMissingRfqItemIds = [],
  } = comparison;

  // Suppliers that submitted a quotation
  const quoted = suppliers.filter((s) => s.quotation);
  // Suppliers still waiting to respond
  const awaiting = suppliers.filter((s) => !s.quotation);

  return (
    <>
      <PageHeader
        title={`Quote Comparison — ${requisition.code}`}
        subtitle={requisition.title}
        breadcrumbs={[
          "Home",
          "Procurement",
          "Requisitions",
          requisition.code,
          "Comparison",
        ]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <RequisitionStatusBadge status={requisition.status} />
            <Button
              variant="ghost"
              onClick={() => navigate(`/requisitions/${requisition.id}`)}
            >
              Back to Requisition
            </Button>
          </div>
        }
      />

      {/* ---------- Summary Cards ---------- */}
      <ComparisonSummaryCards comparison={comparison} />

      {/* ---------- Warning: globally missing items ---------- */}
      {globallyMissingRfqItemIds.length > 0 && (
        <Alert
          variant="warning"
          title="Items no supplier offered"
          className="mb-3"
        >
          <div style={{ fontSize: 13 }}>
            {globallyMissingRfqItemIds.length} item(s) were not offered by any
            supplier. You may need to source them elsewhere or split the order.
          </div>
        </Alert>
      )}

      {/* ---------- Comparison Matrix ---------- */}
      <Card
        title="Comparison Matrix"
        className="mb-3"
        actions={
          <div style={{ fontSize: 12 }} className="text-muted">
            Rows = items · Columns = suppliers · Cheapest highlighted
          </div>
        }
      >
        <ComparisonTable comparison={comparison} />
      </Card>

      {/* ---------- Supplier Overview + Award ---------- */}
      {quoted.length > 0 && (
        <Card title={`Quoted Suppliers (${quoted.length})`} className="mb-3">
          <div className="fh-cmp-suppliers-grid">
            {quoted.map((s) => {
              const isBest =
                s.supplier?.id === comparison.bestOverallSupplierId;
              const coveredPct = items.length
                ? Math.round((s.offeredCount / items.length) * 100)
                : 0;

              return (
                <div
                  key={s.rfqId}
                  className={`fh-cmp-supplier-card ${isBest ? "is-best" : ""}`}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {s.supplier?.name}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11.5 }}>
                        {s.quotation?.code} · {s.rfqCode}
                      </div>
                    </div>
                    {isBest && <Badge variant="success">Best</Badge>}
                  </div>

                  <div
                    className="mt-2"
                    style={{ fontSize: 22, fontWeight: 700 }}
                  >
                    {s.quotation?.currency}{" "}
                    {Number(s.total || 0).toLocaleString()}
                  </div>
                  {(() => {
                    // Compute the average margin across available lines
                    const availableLines = (s.lines || []).filter(
                      (l) => l.status === "available",
                    );
                    if (availableLines.length === 0) return null;

                    const margins = availableLines
                      .map((l) => {
                        // Compare supplier's unit price (which becomes our cost) to the target/selling price
                        // We don't have a target here — but we can show profit vs. market avg price
                        // For now, show the profit per unit vs. our product costPrice if available
                        return null;
                      })
                      .filter(Boolean);

                    // Simpler: show "avg supplier price" as info
                    const avgPrice =
                      availableLines.reduce(
                        (sum, l) => sum + Number(l.unitPrice),
                        0,
                      ) / availableLines.length;
                    return (
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Avg unit price:{" "}
                        <strong>
                          {formatMoney(avgPrice, {
                            symbol: s.quotation?.currency || "IQD",
                          })}
                        </strong>
                      </div>
                    );
                  })()}

                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Covers {s.offeredCount} / {items.length} items ({coveredPct}
                    %)
                    {s.missingCount > 0 && ` · ${s.missingCount} missing`}
                  </div>

                  <div
                    className="mt-1"
                    style={{
                      display: "flex",
                      gap: "1rem",
                      flexWrap: "wrap",
                      fontSize: 12,
                    }}
                  >
                    {s.quotation?.deliveryDays != null && (
                      <span className="text-muted">
                        Delivery: <strong>{s.quotation.deliveryDays}d</strong>
                      </span>
                    )}
                    {s.quotation?.validUntil && (
                      <span className="text-muted">
                        Valid until: <strong>{s.quotation.validUntil}</strong>
                      </span>
                    )}
                  </div>

                  {s.quotation?.paymentTerms && (
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Terms: {s.quotation.paymentTerms}
                    </div>
                  )}

                  <div className="mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      block
                      onClick={() => openAward(s)}
                    >
                      Award to this supplier
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ---------- Awaiting Response ---------- */}
      {awaiting.length > 0 && (
        <Card title={`Awaiting Response (${awaiting.length})`}>
          <div className="d-flex flex-wrap gap-2">
            {awaiting.map((s) => (
              <div
                key={s.rfqId}
                className="d-flex align-items-center gap-2 p-2"
                style={{
                  border: "1px solid var(--fh-border)",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <span>{s.supplier?.name}</span>
                <Badge variant="default">{s.rfqCode}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/rfqs/${s.rfqId}`)}
                >
                  Open RFQ
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ---------- Award Modal ---------- */}
      {awardData && (
        <AwardQuotationModal
          open={awardOpen}
          supplierData={awardData}
          onClose={closeAward}
          onAwarded={handleAwarded}
        />
      )}
    </>
  );
}
